import cookie from 'next-cookies'
import KP5CONSTANTS from './constants'
import {
  getRegistrationDefaultValues,
  registerWithEmailServer,
  getRequestAccessLegalByCountryServer,
  getCurrentUserInvitations
} from './enrollmentAPIs'
import { getCurrentUserServer, getCurrentAmsInfo, getCurrentAmsInfoWithAmsAuth } from './usersAPIs'
import bodyParse from 'urlencoded-body-parser'
import {
  getReturnUrlSetCookieAddString,
  getAmsAuthLoggedInCookieName,
  getProductsWithOneStepRegistration,
  knoxRegistrationProductsWithPathsTileID,
  getKppRedirectforMspKdp,
  getAmsAuthTokenCookieName,
  isValidKpRedirectUrl
} from './helpers'
import { registrationLangCookieToPathPrefix } from './enrollmentHelpers'
import { getAllAcceptedLegalsServer } from './legalAPIs'
import { getMaintenanceMode, getUserDashboardStateServer } from './generalAPIs'
import getConfig from 'next/config'
const { publicRuntimeConfig } = getConfig()

const loggedInCookieName = getAmsAuthLoggedInCookieName()

// Registration cookies expiration time in seconds (1 day)
const REGISTRATION_COOKIES_TTL = 86400

/**
 * @see auth.md for more context on what methods are available
 */

/**
 * @param {function} gSSP a getServerSideProps call
 * @returns {*} invokes getServerSideProps called with {context, remoteUserData}
 */
export function withAuthentication(gSSP) {
  return async (context) => {
    let { data: amsInfo } = await getCurrentAmsInfo(context.req.headers.cookie, [
      'services',
      'userStates'
    ])

    if (context.resolvedUrl === '/dashboard') {
      if (!amsInfo) {
        // NOTE: If AMS user could not be retrieved because KP session does not exist, try using AMS JWT
        // from AMS authToken cookie.
        const amsJwt = cookie(context)[getAmsAuthTokenCookieName()]
        if (amsJwt) {
          const amsInfoResponse = await getCurrentAmsInfoWithAmsAuth(amsJwt, [
            'services',
            'userStates'
          ])
          amsInfo = amsInfoResponse?.data
        }
      }
      // Redirect KDP/MSP users with KPP service to KPP+ dashboard
      let redirectUrl = getKppRedirectforMspKdp(amsInfo?.services)
      if (redirectUrl) {
        return {
          redirect: {
            destination: redirectUrl,
            permanent: false
          }
        }
      }
    }

    const hasValidAmsSession = !!amsInfo

    if (!hasValidAmsSession || !cookie(context)[loggedInCookieName]) {
      if (context.resolvedUrl) {
        context.res.setHeader(
          'Set-Cookie',
          getReturnUrlSetCookieAddString(`/${context.locale}${context.resolvedUrl}`)
        )
      }

      return {
        redirect: {
          destination: `/${context.locale}/login`,
          permanent: false
        }
      }
    }

    let loggedInUser = null
    const response = await getCurrentUserServer(context.req.headers.cookie, true)
    if (response.data) {
      loggedInUser = response.data
    }

    if (!loggedInUser) {
      // NOTE: This is unexpected. This was the source of infinite redirect issues in prod. After USP logout, the
      // KP session would continue to exist, causing KP5 Tenants Users GET /api/v1/account/current to return 401
      // error due to AMS 4010006 error (the user has logged out of AMS and all AMS JWTs are now invalid). There
      // is also another scenario where GET /api/v1/account/current was sometimes returning a 404 here. We haven't
      // been able to replicate the 404 so far.

      // TODO: If the KP user exists only in Stg/QA/Prod and not locally, this logic will be triggered.
      //  Make this logic better, and figure out a way to create the local KP user in registerFinish API
      //  instead of return 400 error.
      console.log(
        'Session sync mechanism triggered. AMS authToken cookie references a valid AMS session, but getCurrentUserServer was not successful.'
      )
      return {
        redirect: {
          destination: `/${context.locale}/sso/logoutFinish?returnUrl=/${context.locale}/login`,
          permanent: false
        }
      }
    }

    // Redirect to USP if there's no active service
    const hasActiveService = amsInfo?.userStates.some((service) => service.userState === 'Active')

    const isAccessiblePage =
      [
        '/kme-direct-download',
        '/knox-efota-on-premises-resources',
        '/knox-efota-on-premises-resources/download-firmware-files',
        '/knox-efota-on-premises-resources/download-license-files',
        '/dashboard'
      ].indexOf(context.resolvedUrl) > -1

    if (!hasActiveService && !isAccessiblePage) {
      return {
        redirect: {
          destination: publicRuntimeConfig.uspBaseUrl,
          permanent: false
        }
      }
    }

    // Redirect to maintenance page if maintenanceMode
    const maintenanceResponse = await getMaintenanceMode(context.req.headers.cookie)
    if (maintenanceResponse.statusCode == 200 && maintenanceResponse.data) {
      return {
        redirect: {
          destination: `/${context.locale}/sso/logoutFinish?returnUrl=/${context.locale}/maintenance`,
          permanent: false
        }
      }
    }

    return gSSP({ context, loggedInUser })
  }
}

/**
 * @param {function} gSSP a getServerSideProps call
 * @param productParam
 * @returns {*} invokes getServerSideProps called with {context, remoteUserData}
 */
export function withRequestAccess(gSSP, productParam) {
  return async (context) => {
    // Redirect to maintenance page if maintenanceMode
    const maintenanceResponse = await getMaintenanceMode(context.req.headers.cookie)
    if (maintenanceResponse.statusCode == 200 && maintenanceResponse.data) {
      return {
        redirect: {
          destination: cookie(context)[loggedInCookieName]
            ? `/${context.locale}/sso/logoutFinish?returnUrl=/en/maintenance`
            : `/${context.locale}/maintenance`,
          permanent: false
        }
      }
    }

    if (!cookie(context)[loggedInCookieName]) {
      if (context.resolvedUrl) {
        context.res.setHeader(
          'Set-Cookie',
          getReturnUrlSetCookieAddString(`/${context.locale}${context.resolvedUrl}`)
        )
      }
      return {
        redirect: {
          destination: `/${context.locale}/login`,
          permanent: false
        }
      }
    }
    let loggedInUser = null
    const userResponse = await getCurrentUserServer(context.req.headers.cookie, true)
    if (userResponse.data) {
      loggedInUser = userResponse.data
    }
    // NOTE: Allow /account/request-access/multiservice to be idempotent. undefined vars are
    // not serializable and cannot be returned as gSSP prop.
    let productParams = []
    // check if has multiservice kcs invitation
    if (productParam === 'multiservice') {
      const invitationResponse = await getCurrentUserInvitations(context.req.headers.cookie)
      if (invitationResponse.data?.length) {
        productParams = invitationResponse.data
      }
    }

    let legalDocsResponse = null
    const legalResponse = await getRequestAccessLegalByCountryServer(
      context.req.headers.cookie,
      productParams.length > 0 ? productParams : productParam
    )
    if (legalResponse.data) {
      legalDocsResponse = legalResponse.data
    }

    return gSSP({ context, loggedInUser, legalDocsResponse, productParams })
  }
}

export function withRegistrationRouter(gSSP) {
  return async (context) => {
    // IMPORTANT: None of the pages using withRegistrationRouter should be indexed. They are internal
    // routing pages that many times lead to B2C sub-admin register page. Prevent all routes from being
    // indexed.
    context.res.setHeader('X-Robots-Tag', 'noindex')

    let redirectPath

    let productParam = (context.params.productParam && context.params.productParam[0]) || null
    if (cookie(context)[loggedInCookieName]) {
      // Implementation of detecting user if has pending multiservice kcs invitation
      const invitationResponse = await getCurrentUserInvitations(context.req.headers.cookie)

      if (invitationResponse.data) {
        let invitationData = invitationResponse.data

        // User has multi service registration
        if (invitationData && invitationData.length > 1) {
          return gSSP({ redirectPath: `/${context.locale}/account/request-access/multiservice` })
        } else if (invitationData.length === 1) {
          productParam = invitationData[0]
        }
      }

      // If the user is fully registered, we need to send to request-access flow.
      const acceptedLegalResponse = await getAllAcceptedLegalsServer(context.req.headers.cookie)
      const termsAcceptanceRecord = acceptedLegalResponse.data?.find(
        (acceptance) => acceptance.legalType === 'registration_terms_use'
      )
      if (termsAcceptanceRecord) {
        // Add km product for login user. Fix for sub admin is logged in and invited by super admin in km
        // Subadmin received an email with a path: /en/user/register/km
        // User has registration_terms_use record.
        const hasOwnPath =
          productParam && KP5CONSTANTS.REGISTER_SUBADMIN_PRODUCTS_WITH_PATHS.includes(productParam)
        let redirectPath = `/${context.locale}/account/request-access`
        if (hasOwnPath) {
          redirectPath = `/${context.locale}/account/request-access/${productParam}`
        }
        if (getProductsWithOneStepRegistration().includes(productParam)) {
          redirectPath = `/en/dashboard`
        }
        return gSSP({ redirectPath })
      }
    }

    // Sub-admin user is logged out or has not finished registration.
    redirectPath = `/${context.locale}/register?isSubAdminPath=1`

    // Before the redirect set the registrationReason
    if (!cookie(context)['registrationReason']) {
      const expires = new Date(new Date().getTime() + REGISTRATION_COOKIES_TTL * 1000).toUTCString()
      const cookies = `registrationReason=${encodeURIComponent(
        KP5CONSTANTS.REGISTRATION_REASONS.SUB_ADMIN_REGISTRATION
      )}; Max-Age=${REGISTRATION_COOKIES_TTL}; Expires=${expires}; Secure; SameSite=Lax; Path=/`
      context.res.setHeader('Set-Cookie', cookies)
    }
    return gSSP({ redirectPath, context })
  }
}

/**
 * Adds necessary admin dashboard authentication to getServerSideProps call.
 *  1. Fetch user (and tenant) data
 *  2. Check if the required permissions or roles list is met
 *  3. Call gSSP
 *
 * @param {function} gSSP a getServerSideProps call
 * @param requiredPermissions
 * @param requiredRoles
 * @returns {(function(*=): Promise<{redirect: {permanent: boolean, destination: string}}|{redirect: {permanent: boolean, destination: string}}|*>)|*}
 */
export function withAdminAuthentication(
  gSSP,
  requiredPermissions = [],
  requiredRoles = [],
  destination
) {
  return async (context) => {
    let loggedInUser = null

    // If the user is not logged in, redirect to login.
    if (!cookie(context).kpLoggedIn_admin) {
      return {
        redirect: {
          destination: `/login/corp-ad`,
          permanent: false
        }
      }
    } else {
      const response = await getCurrentUserServer(context.req.headers.cookie, true)
      if (response instanceof Error) {
        if (response.code === 401) {
          // NOTE: Response code 401 likely means this AMS session got killed because they signed in
          // to another session with the same user.
          return {
            redirect: {
              destination: `/sso/logoutFinish`,
              permanent: false
            }
          }
        }
      }
      if (response.data) {
        loggedInUser = response.data
        // Roles will be checked first
        if (requiredRoles.length > 0) {
          if (!requiredRoles.some((rr) => loggedInUser.roles.map((r) => r.roleName).includes(rr))) {
            return {
              redirect: {
                destination: destination || '/no-access',
                permanent: false
              }
            }
          }
        }
        // The user is approved if each of requiredPermissions is in their permissions array!
        if (requiredPermissions.length > 0) {
          if (!requiredPermissions.every((rp) => loggedInUser.permissions.includes(rp))) {
            return {
              redirect: {
                destination: destination || '/no-access',
                permanent: false
              }
            }
          }
        }
      } else {
        // getCurrentUser returned non-200. If 404, the logged in SA user doesn't exist in KP.
        // Prevent an infinite auth loop by sending user to error page where they can choose to
        // sign in with a different user.
        return {
          redirect: {
            destination: '/no-access',
            permanent: false
          }
        }
      }
    }
    return gSSP({ context, loggedInUser })
  }
}

/**
 * HOC to support both logged in and not logged in states for pages for navigation to show correct state
 * @param {function} gSSP a getServerSideProps call
 * @returns {*} invokes getServerSideProps called with {context, remoteUserData}
 */
export const withOpenAccessPage = (gSSP) => {
  return async (context) => {
    if (!cookie(context)[loggedInCookieName]) {
      return gSSP({ context })
    }
    let loggedInUser = null
    const response = await getCurrentUserServer(context.req.headers.cookie, true)
    if (response.data) {
      loggedInUser = response.data
    }
    return gSSP({ context, loggedInUser })
  }
}
/**
 * @param {function} gSSP a getServerSideProps call
 * @returns {*} invokes getServerSideProps called with {context, remoteUserData}
 */
export const withRegistration = (gSSP) => {
  return async (context) => {
    // Redirect unsupported products to 404.
    let productParam = (context.params.productParam && context.params.productParam[0]) || null
    let isSubAdminPath = !!context.query.isSubAdminPath
    const postData = await bodyParse(context.req)
    productParam = postData.productParam ? postData.productParam : productParam

    /**
     * KP5-3527 will also add Registration Reason to the registration form
     */
    let registrationReason = ''
    if (!postData.registrationReason) {
      // If registrationReason is not provided in postData, use the one from cookie
      if (cookie(context)['registrationReason']) {
        registrationReason = cookie(context)['registrationReason']
      } else if (isSubAdminPath) {
        registrationReason = KP5CONSTANTS.REGISTRATION_REASONS.SUB_ADMIN_REGISTRATION
      }
    } else {
      registrationReason = postData.registrationReason
    }

    // all KS products will return 'ks' as product param (KP5-2602)
    const knoxSuiteProducts = [
      KP5CONSTANTS.REGISTER_PRODUCTS.SUITE,
      KP5CONSTANTS.REGISTER_PRODUCTS.MANAGE,
      KP5CONSTANTS.REGISTER_PRODUCTS.PLATFORM_FOR_ENTERPRISE,
      KP5CONSTANTS.REGISTER_PRODUCTS.EFOTA_ONE,
      KP5CONSTANTS.REGISTER_PRODUCTS.MOBILE_ENROLLMENT,
      KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE,
      KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE
    ]
    if (
      knoxSuiteProducts.some((product) => {
        return product === productParam
      })
    ) {
      productParam = KP5CONSTANTS.REGISTER_PRODUCTS.SUITE
    }

    // Get user selected locale.
    // Give preference to postData since this is the lang that user selected in KnoxPortal.
    let langPrefix = postData.userSelectedLang || context.locale

    // Redirect to maintenance page if maintenanceMode
    const maintenanceResponse = await getMaintenanceMode(context.req.headers.cookie)
    if (maintenanceResponse.statusCode == 200 && maintenanceResponse.data) {
      return {
        redirect: {
          destination: cookie(context)[loggedInCookieName]
            ? `/${langPrefix}/sso/logoutFinish?returnUrl=/${langPrefix}/maintenance`
            : `/${langPrefix}/maintenance`,
          permanent: false
        }
      }
    }

    if (
      productParam &&
      !KP5CONSTANTS.REGISTER_SUBADMIN_PRODUCTS_WITH_PATHS.includes(productParam)
    ) {
      return {
        redirect: {
          destination: `/${langPrefix}/404`,
          permanent: false
        }
      }
    }

    if (productParam === KP5CONSTANTS.REGISTER_PRODUCTS.DEPLOYMENT_PROGRAM) {
      return {
        redirect: {
          destination: `${publicRuntimeConfig.partnerBaseUrl}/enrollment/kdp`,
          permanent: false
        }
      }
    }

    if (cookie(context)[loggedInCookieName]) {
      // Get the registrationReason from the cookie
      if (cookie(context)['registrationReason']) {
        registrationReason = cookie(context)['registrationReason']
      }

      // Redirect to /dashboard if product is Pending or Active
      const productStates = await getUserDashboardStateServer(context.req.headers.cookie)
      const product = productStates.data?.find(
        (item) => item.tileId === knoxRegistrationProductsWithPathsTileID(productParam)
      )
      if (product && ['Pending', 'Active'].includes(product.status)) {
        let returnUrl = cookie(context)['returnUrl']
        if (!returnUrl) {
          returnUrl = `/${langPrefix}/dashboard` // TODO: add Support for locale, see KP5-2217
        }

        return {
          redirect: {
            destination: isValidKpRedirectUrl(returnUrl) ? returnUrl : `/en/404`,
            permanent: false
          }
        }
      }

      // Get registration default values for registration form.
      const remoteUserData = await getRegistrationDefaultValues(
        context.req.headers.cookie,
        productParam
      )
      if (remoteUserData?.data?.isInvited) {
        // FOR SUB ADMIN
        // If the user is fully registered, we need to send to request-access flow.
        const acceptedLegalResponse = await getAllAcceptedLegalsServer(context.req.headers.cookie)
        const termsAcceptanceRecord = acceptedLegalResponse.data?.find(
          (acceptance) => acceptance.legalType === 'registration_terms_use'
        )
        if (termsAcceptanceRecord) {
          // User has registration_terms_use record.
          const hasOwnPath =
            productParam && KP5CONSTANTS.REGISTER_PRODUCTS_WITH_PATHS.includes(productParam)
          let redirectPath = `/${langPrefix}/account/request-access` // TODO: add Support for locale, see KP5-2217
          if (hasOwnPath) {
            redirectPath = `/${langPrefix}/account/request-access/${productParam}` // TODO: add Support for locale, see KP5-2217
          }
          if (getProductsWithOneStepRegistration().includes(productParam)) {
            redirectPath = `/${langPrefix}/dashboard` // TODO: add Support for locale, see KP5-2217
          }
          return {
            redirect: {
              destination: redirectPath,
              permanent: false
            }
          }
        }
      }

      if (remoteUserData.data) {
        const registrationData = remoteUserData.data.defaultValues
        registrationData.isInvited = remoteUserData.data.isInvited
        registrationData.canModifyCountry = remoteUserData.data.canModifyCountry
        registrationData.canModifyTenantFields = remoteUserData.data.canModifyTenantFields
        registrationData.canModifyProvince = remoteUserData.data.canModifyProvince
        registrationData.canModifyZipcode = remoteUserData.data.canModifyZipcode
        registrationData.supportedCountries = remoteUserData.data.supportedCountries
        registrationData.isRegistering = true
        registrationData.langPrefix = langPrefix
        registrationData.registrationReason = registrationReason
        registrationData.productParam = productParam

        // KP5- 1904 - include mspInfo for subadmins
        if (remoteUserData.data.mspInfo) registrationData.mspInfo = remoteUserData.data.mspInfo

        return gSSP({
          context,
          productParam,
          registrationData,
          step: 2,
          loggedInUser: registrationData,
          langPrefix,
          registrationReason
        }) // if we are logged in we go back to step 2, step number is passed down at page level
      } else {
        // Redirect KDP/MSP users with KPP service to KPP+ dashboard
        const { data: amsInfo } = await getCurrentAmsInfo(context.req.headers.cookie, ['services'])
        let redirectUrl = getKppRedirectforMspKdp(amsInfo?.services)
        if (redirectUrl) {
          return {
            redirect: {
              destination: redirectUrl,
              permanent: false
            }
          }
        }

        return gSSP({
          context,
          productParam,
          errorCode: remoteUserData.error || null,
          langPrefix,
          registrationReason
        })
      }
    }

    // Support registration flow initiation from KP4 solution pages. This is a direct
    // replacement for KP2 behavior and allows a POST request with "email" body field.
    // The user is automatically forwarded on to SA sign in or sign up.
    if (postData.email) {
      const response = await registerWithEmailServer(postData.email)
      if (response.data) {
        let redirectUrl = `/${langPrefix}/sso/signup?email=${encodeURIComponent(
          postData.email
        )}&locale=${encodeURIComponent(langPrefix)}`

        if (response.data.saExists === true) {
          redirectUrl = `/${langPrefix}/login/samsung?locale=${encodeURIComponent(langPrefix)}`
        }

        // Registration cookies expiration
        const expires = new Date(
          new Date().getTime() + REGISTRATION_COOKIES_TTL * 1000
        ).toUTCString()
        let cookies = []
        if (langPrefix) {
          cookies.push(
            `registrationLang=${encodeURIComponent(
              langPrefix
            )}; Max-Age=${REGISTRATION_COOKIES_TTL}; Expires=${expires}; Secure; SameSite=Lax; Path=/;`
          )
        }

        if (productParam) {
          // Existing cookie
          cookies.push(
            `registrationProduct=${encodeURIComponent(
              productParam
            )}; Max-Age=${REGISTRATION_COOKIES_TTL}; Expires=${expires}; Secure; SameSite=Lax; Path=/;`
          )
          // New cookie
          context.res.setHeader('Set-Cookie', cookies)
        }

        if (registrationReason) {
          // Existing cookie
          cookies.push(
            `registrationReason=${encodeURIComponent(
              registrationReason
            )}; Max-Age=${REGISTRATION_COOKIES_TTL}; Expires=${expires}; Secure; SameSite=Lax; Path=/;`
          )
          // New cookie
          context.res.setHeader('Set-Cookie', cookies)
        }

        return {
          redirect: {
            destination: redirectUrl,
            statusCode: 302
          }
        }
      }
    }

    // If user is not logged in and not trying to register as MSP/KDP user or as sub-admin, redirect to KP4's common registration page
    // let isSubAdminPath = !!context.query.isSubAdminPath
    if (
      !cookie(context)[loggedInCookieName] &&
      !KP5CONSTANTS.REGISTER_PRODUCTS_WITH_HIDDEN_PATHS.HIDDEN.includes(productParam) &&
      (!KP5CONSTANTS.REGISTER_PRODUCTS_WITH_HIDDEN_PATHS.OTHERS.includes(productParam) ||
        !isSubAdminPath)
    ) {
      return {
        redirect: {
          destination: `${publicRuntimeConfig.kp4BaseUrl}/${langPrefix}/register`,
          permanent: false
        }
      }
    }

    // As part of KP5-2217
    // We store the original lang -> because redirect from SA will always be to /en.

    if (!cookie(context)['registrationLang']) {
      const expires = new Date(new Date().getTime() + REGISTRATION_COOKIES_TTL * 1000).toUTCString()
      const cookies = `registrationLang=${encodeURIComponent(
        langPrefix
      )}; Max-Age=${REGISTRATION_COOKIES_TTL}; Expires=${expires}; Secure; SameSite=Lax; Path=/`
      context.res.setHeader('Set-Cookie', cookies)
    } else {
      // I have a cookie so please consider the old registration cookie
      langPrefix = registrationLangCookieToPathPrefix(cookie(context)['registrationLang'])
    }

    return gSSP({ context, productParam, langPrefix, registrationReason })
  }
}

/**
 *
 * @param {*} cookies : client cookies
 * @returns {boolean} : returns true if the session is active, otherwise false
 */
export const getSessionDetails = (context) => {
  const allCookies = cookie(context)
  /**
   * Based on the cookies we determine if the session is still active
   * if either -> kpUserToken_jwt is present
   * or
   * if either -> [env]-authToken is present
   */
  return {
    isValidKpSession: !!allCookies.kpUserToken_jwt,
    isValidAmsSession: allCookies[getAmsAuthTokenCookieName()]
  }
}

export const logoutFromB2BAccount = (redirectUrl) => {
  // For B2B users, we need to clear both KP5 sessions and UA service sessions
  // Step 1: Clear KP5 sessions first
  // Step 2: Then redirect to UA B2B logout to clear Samsung Account Business session

  const finalRedirectUrl = redirectUrl || publicRuntimeConfig.partnerBaseUrl

  // First, clear KP5 sessions by calling signoutFinish (POST method)
  // Then redirect to UA B2B logout regardless of success/failure
  let signOutUrl = '/api/v1/sso/signoutFinish'
  if (publicRuntimeConfig.remoteBackendBaseUrl) {
    signOutUrl = `${publicRuntimeConfig.remoteBackendBaseUrl}${signOutUrl}`
  }

  fetch(signOutUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  }).finally(() => {
    // Always redirect to UA B2B logout after attempting KP5 session cleanup
    const uaB2BLogoutUrl = `${
      publicRuntimeConfig.uspUABaseUrl
    }/signout?redirectUrl=${encodeURIComponent(finalRedirectUrl)}`
    window.location.href = uaB2BLogoutUrl
  })
}
